import axios from "axios";
import DOMPurify from "dompurify";
import React, { useEffect, useState } from "react";
import { ChatMessageVideoInterface } from "../interfaces/ChatMessageVideo";
import ChatMessage from "./ChatMessage";
import ChatMessageButton from "./ChatMessageButton";
import ChatMessageInput from "./ChatMessageInput";
import TypingAnimation from "./TypingAnimation";

const ChatMessageVideo: React.FC<ChatMessageVideoInterface> = ({
    triggerNextStep,
    step,
    currentStep,
    scrollToBottom,
    disableScrollToBottom,
    videoLink,
    values,
    handleInputSubmit,
    setDataValues,
    disableTypingAnimation = false,
}) => {
    const { input, end, delay, message, options } = step!;
    const [videoBlob, setVideoBlob] = useState<string>("");

    const videoInit = async () => {
        try {
            const response = await axios.get(videoLink);
            if (response.data) {
                if (response.data?.url) {
                    setVideoBlob(response.data.url);
                } else {
                    const responseBlob = await axios.get(videoLink, { responseType: "blob" });
                    const blobUrl = URL.createObjectURL(responseBlob.data);
                    setVideoBlob(blobUrl);
                }
            }
        } catch (error) {
            console.error({ error });
        }
    };

    useEffect(() => {
        if (!disableScrollToBottom && step === currentStep) {
            scrollToBottom!();
        }
    });

    useEffect(() => {
        setTimeout(() => {
            if (!input && !end && !options) {
                triggerNextStep!({
                    currentStep: step!,
                    trigger: step?.trigger!,
                });
            }
        }, delay || 1000);
        /*eslint-disable react-hooks/exhaustive-deps*/
        videoInit();
    }, []);

    return (
        <React.Fragment>
            <ChatMessage
                step={step}
                hideAvatar={step === currentStep ? (input || message ? true : false) : true}
                scrollToBottom={scrollToBottom}
                disableTypingAnimation={disableTypingAnimation}
                bubbleStyle={{
                    padding: 0,
                }}
                render={() => (
                    <>
                        {videoBlob ? (
                            <div className="conversation-message-bubble-video">
                                <video
                                    id={step?.id}
                                    width="100%"
                                    height="100%"
                                    controls
                                    playsInline
                                >
                                    <source src={videoBlob} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        ) : (
                            <div className="conversation-message-bubble">
                                <TypingAnimation
                                    theme={step?.theme!}
                                    type={step?.type!}
                                    infinite={true}
                                />
                            </div>
                        )}
                    </>
                )}
            />

            {options?.length ? (
                <ChatMessageButton
                    currentStep={currentStep}
                    hideAvatar={false}
                    triggerNextStep={triggerNextStep}
                    values={values}
                    step={step}
                    disableScrollToBottom={step?.disableScrollToBottom}
                    handleInputSubmit={handleInputSubmit}
                    setDataValues={setDataValues}
                    scrollToBottom={scrollToBottom}
                />
            ) : (
                <>
                    {message && (
                        <ChatMessage
                            step={step}
                            disableTypingAnimation={disableTypingAnimation}
                            hideAvatar={input ? false : true}
                            scrollToBottom={scrollToBottom}
                            render={() => (
                                <div
                                    className="conversation-message-bubble-text"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(message!),
                                    }}
                                />
                            )}
                        />
                    )}

                    {input && (
                        <ChatMessageInput
                            triggerNextStep={triggerNextStep}
                            step={step}
                            currentStep={currentStep}
                            hideAvatar={step === currentStep ? false : true}
                            disableTypingAnimation={disableTypingAnimation}
                            values={values}
                            handleInputSubmit={handleInputSubmit}
                            setDataValues={setDataValues}
                        />
                    )}
                </>
            )}
        </React.Fragment>
    );
};

export default React.memo(ChatMessageVideo);
